import {
  bindingCandidate,
  controlReasonsOf,
  controlSentenceOf,
  licencePostureOf,
  primaryControlOf,
  usmlCategory,
} from 'components/ExportDesign/exportDesignControl';
import {
  DESIGN_CHAT_TRANSCRIPT_KEY,
  findReportTurnIndex,
  savedDesignChatTranscript,
} from 'components/ExportDesign/exportDesignChatTranscript';
import { humanizeKey, humanizeSpecRef } from 'components/ExportDesign/exportDesignProgress';
import {
  DesignTicketPayload,
  LICENSING_IMPLICATION,
  TicketChatMessage,
  TicketDesignChanges,
  TicketRung,
} from 'components/ExportDesign/ExportDesignTicket';

// --- Saved-ticket contract for the Design Assistant.
//
// FRONTEND ONLY. This module is the seam: `buildDesignTicketCase` produces the document the
// client will POST, and `readDesignTicket` turns a saved document back into what the ticket
// renders. Nothing here calls an API — ExportDesignChatUI does that through CasesAPI, exactly
// as EARChatUI's `handleTicket` does (EARChatUI.tsx:3309).
//
// ============================ STATUS ============================
//
// No backend changes were needed — the Case schema (server/server/api/models/Case/Case.ts)
// has no docType enum, so a new value just works. What DID need doing, and is now done:
//   1. Accept `docType: DESIGN_TICKET_DOC_TYPE` on POST/PUT /api/v1/cases — already worked,
//      no server change required.
//   2. List DESIGN_TICKET_DOC_TYPE in Monitor's AUTO_TICKETS (pages/monitor/Monitor.tsx).
//   3. Dispatch it on the ticket page to <ExportDesignTicket> (pages/monitor/Ticket.tsx).
// NOTE Mongoose runs strict:true, so any field NOT in the schema is silently dropped;
// `initialRequest` is already lost that way on export tickets (EARChatUI.tsx writes it, the
// schema has no such path) — this payload avoids that by keeping the whole assessment as ONE
// json string, `workflowData[0]._workflowStateJson`, the same trick the export workflow uses
// (EARChatUI.tsx:3385).
//
// ==================================================================

export const DESIGN_TICKET_DOC_TYPE = 'Design Assistant';
export const DESIGN_TICKET_WORKFLOW_TYPE = 'export-design';

const toJsonSafe = <T,>(value: T): T => {
  try {
    return JSON.parse(JSON.stringify(value ?? null));
  } catch (_error) {
    return value;
  }
};

const str = (v: any): string => (typeof v === 'string' ? v.trim() : v === null || v === undefined ? '' : String(v));

/**
 * The wall-clock time ("HH:MM") of a server timestamp, in the viewer's own time zone. The server stamps
 * turns in UTC, and printing that as it came made every reader outside UTC see the wrong hour (a turn
 * at 12:07 their time read 10:07). '—' when there is no usable time.
 */
export const localClockTime = (ts: unknown): string => {
  const raw = str(ts);
  const date = new Date(raw);
  if (!raw || Number.isNaN(date.getTime())) return '—';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

/**
 * The calendar date ("YYYY-MM-DD") of a server timestamp, in the viewer's own time zone — for the same
 * reason as localClockTime: the server stamps in UTC, so near midnight the UTC date is the wrong day for
 * everyone else. '' when there is no usable time.
 */
export const localCalendarDate = (ts: unknown): string => {
  const raw = str(ts);
  const date = new Date(raw);
  if (!raw || Number.isNaN(date.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Unwrap a versioned ledger entry — facts and specs are {value, source, version, updated_at}. */
const entryValue = (entry: any): string =>
  entry && typeof entry === 'object' ? str(entry.value) : str(entry);

const factValue = (state: any, name: string): string =>
  entryValue((state?.facts || {})[name]);

/** "router.map_specs" / "classifier" -> something a reader can act on. */
const sourceLabel = (source: string): string => {
  const s = (source || '').toLowerCase();
  if (!s) return '';
  if (s.startsWith('router')) return 'stated by you';
  // Read off an attached picture by the router. Small print is sometimes misread, so the ticket
  // says where the value came from rather than claiming the customer stated it.
  if (s.startsWith('attachment.image')) return 'read from your image — please confirm';
  if (s.startsWith('classifier')) return 'derived by the classifier';
  if (s.includes('attachment') || s.includes('document')) return 'from an attached document';
  return `source: ${source}`;
};

export interface BuildDesignTicketArgs {
  /** Present on re-save; omit to create. */
  existingId?: string;
  ticketName: string;
  /** The round-tripped workflow_state — facts, spec_sheet, classification, delta, ladder,
   *  licensing, report, conversation, _meta. */
  workflowState: Record<string, any>;
  /** Emails chosen in CreateTicketWizard. */
  sharedWith: string[];
  frequency: string;
  curUser: any;
  /** The user's opening message, kept verbatim. */
  initialRequest?: string;
  /** The chat as shown, already serialized (serializeDesignChatTranscript); '' or absent stores none. */
  chatTranscript?: string;
  /** Overrides the status read from the state: a save for a background run is In Progress (S8b) until
   *  the run finishes and the server sets Complete / Needs Review. */
  status?: string;
}

/**
 * The document POSTed to /api/v1/cases. Field-for-field a sibling of EARChatUI's `saveTicket`
 * (EARChatUI.tsx:3371) so it lands in the same collection with the same listing behaviour.
 */
export const buildDesignTicketCase = ({
  existingId,
  ticketName,
  workflowState,
  sharedWith,
  frequency,
  curUser,
  initialRequest,
  chatTranscript,
  status: statusOverride,
}: BuildDesignTicketArgs): Record<string, any> => {
  const state = workflowState || {};
  const classification = (state.classification || {}).result || {};
  const owner = {
    kc_id: curUser?.basics?.id,
    email: curUser?.basics?.email,
    firstName: curUser?.basics?.firstName,
    lastName: curUser?.basics?.lastName,
  };
  const eccns = str(factValue(state, 'candidate_eccns'));
  const productDescription =
    factValue(state, 'product_description') || factValue(state, 'product_name');

  // A saved assessment is Complete once a full report exists — the report is the artifact
  // that consolidates everything. Anything earlier is still in progress. Mirrors the export
  // ticket's two-value status (there is no approval state on a case).
  const status = statusOverride || (state.report ? 'Complete' : 'In Progress');

  const lastUpdate = new Date().toISOString().slice(0, 10); // yyyy-MM-dd, as the export ticket

  return {
    ...(existingId ? { _id: existingId } : {}),
    owner: owner,
    ticketName: ticketName || 'Design Assistant',
    lastUpdate: lastUpdate,
    activityType: 'workflow',
    workflowType: DESIGN_TICKET_WORKFLOW_TYPE,
    docType: DESIGN_TICKET_DOC_TYPE,
    sharedWith: sharedWith,
    frequency: frequency,
    activeFilter: '',
    selectedOpts: [],
    status: status,
    favorite: false,
    // Every Monitor/Saved Work query scopes by groupId (see pages/monitor/Monitor.tsx's
    // getCasesFromAPI etc.), not groupName — groupName alone is just a display label.
    // Without this, the case saves fine but is invisible everywhere in Saved Work, since
    // Mongo's groupId: <id> match never matches a document missing the field entirely.
    // Mirrors EARChatUI.handleTicket, which sets this the same way (EARChatUI.tsx:3312).
    groupId: curUser?.selected_team?.id,
    groupName: curUser?.selected_team?.name,
    classificationValue: productDescription,
    initialRequest: initialRequest || productDescription,
    // The assessment's own Q&A lives in `conversation`; followUps stays empty so the shared
    // Monitor card/PDF code paths have the shape they expect.
    followUps: [],
    // The raw socket frames are not saved: the chat clears them every turn, so they held only the last
    // turn, nothing here reads them, and each frame repeated the whole workflow state. The chat itself
    // is saved below, in a form the ticket can show and Continue can restore.
    aiStream: [],
    // ONE blob. Everything the ticket renders comes back out of this — the chat rides inside it
    // because the Case schema has no field for it (top-level extras are dropped), as Export's does.
    workflowData: [
      toJsonSafe({
        _workflowStateJson: JSON.stringify(state),
        ...(chatTranscript ? { [DESIGN_CHAT_TRANSCRIPT_KEY]: chatTranscript } : {}),
      }),
    ],
    exNotes: '',
    chapterNotes: '',
    crossRulings: [],
    commodity: {
      commodityName: productDescription,
      harmonizedScheduleCode: '',
      // An EAR-listed result saves the ECCN that binds, not the first one considered (S12 R-10).
      eccn:
        (classification.current_regime === 'EAR-listed' && primaryControlOf(classification)) ||
        (classification.candidate_eccns || [])[0] ||
        eccns.split(',')[0]?.trim() ||
        '',
      destinationCountry: factValue(state, 'destination_country'),
    },
    activity: {
      user: owner,
      action: existingId ? 'updated saved design assessment' : 'created design assessment',
      timeStamp: Date.now(),
    },
  };
};

export interface ShareUpdate {
  /** The emails that are actually new (de-duplicated, and not already on the ticket). */
  newEmails: string[];
  /** The complete `sharedWith` list to write — the case's own list plus the new emails. */
  sharedWith: string[];
  /** The complete `activity` list to write — the case's own history plus this share. */
  activity: any[];
}

/**
 * What to PUT when a saved ticket is shared. The server applies the update as given
 * (findByIdAndUpdate), so `activity` and `sharedWith` REPLACE what is stored: both are built from
 * the case as it is stored now, never from a local mirror. (A local list that starts empty would
 * wipe the ticket's earlier history — "created design assessment", prior saves — on the first
 * share.) Returns null when there is nobody new to share with.
 */
export const buildShareUpdate = ({
  savedCase,
  emails,
  actor,
  now = Date.now(),
}: {
  savedCase: Record<string, any>;
  emails: string[];
  actor: Record<string, any>;
  now?: number;
}): ShareUpdate | null => {
  const currentShared: string[] = Array.isArray(savedCase?.sharedWith) ? savedCase.sharedWith : [];
  const newEmails = emails.filter(
    (email, index) => email && !currentShared.includes(email) && emails.indexOf(email) === index,
  );
  if (newEmails.length === 0) return null;
  // The create path sends `activity` as one object (mongoose casts it to a one-entry array), so a
  // stored case can legitimately hand back either shape.
  const currentActivity: any[] = Array.isArray(savedCase?.activity)
    ? savedCase.activity
    : savedCase?.activity
    ? [savedCase.activity]
    : [];
  return {
    newEmails: newEmails,
    sharedWith: [...currentShared, ...newEmails],
    activity: [
      ...currentActivity,
      { user: actor, action: `shared with ${newEmails.join(', ')}`, timeStamp: now },
    ],
  };
};

/** Pull workflow_state back out of a saved case. Tolerates the legacy/alternate shapes the
 *  export ticket also guards against (EARChatUI.tsx:268). */
export const readWorkflowState = (savedCase: Record<string, any>): Record<string, any> => {
  const wd = savedCase?.workflowData;
  const snapshot = Array.isArray(wd) ? wd[0] : wd;
  const raw =
    snapshot?._workflowStateJson || snapshot?.workflowStateJson || savedCase?.workflowState;
  if (!raw) return {};
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch (_error) {
    return {};
  }
};

const REGIMES: DesignTicketPayload['regime'][] = ['ITAR', 'EAR-listed', 'EAR99', 'uncertain'];

/**
 * Saved case -> what <ExportDesignTicket> renders. Every field traces to workflow_state; the
 * three the tool does not yet hold (reference, regulationEdition, supersededNote) are left
 * undefined here and the component labels them `proposed` when a value does arrive.
 */
export const readDesignTicket = (savedCase: Record<string, any>): DesignTicketPayload => {
  const state = readWorkflowState(savedCase);
  const classificationBlock = state.classification || {};
  const classification = classificationBlock.result || {};
  const verifier = classificationBlock.verifier || null;
  const report = state.report || {};
  const licensing = state.licensing || null;
  const ladder = state.ladder || null;
  const delta = state.delta || null;
  const meta = state._meta || {};

  const specSheet = state.spec_sheet || {};
  const norm = (v: string): string => v.toLowerCase().replace(/\s+/g, ' ').trim();
  const intendedUse = factValue(state, 'intended_use') || undefined;
  // The router sometimes also files the intended use as a spec ("application: commercial
  // industrial inspection"); Stage 1 already shows it as the intended use, so don't list it twice.
  const specs = Object.keys(specSheet)
    .map((key) => ({
      label: humanizeKey(key),
      value: entryValue(specSheet[key]),
      source: sourceLabel(specSheet[key]?.source),
    }))
    .filter((spec) => !intendedUse || norm(spec.value) !== norm(intendedUse));

  const regimeRaw = str(classification.current_regime) as DesignTicketPayload['regime'];
  const regime = REGIMES.indexOf(regimeRaw) >= 0 ? regimeRaw : 'uncertain';

  // Depends on the actual outcome — "USML checked, no match" is only true for a non-ITAR
  // result; an ITAR determination means the opposite happened, and an unsettled regime means
  // neither claim is safe to make yet.
  const usmlMatch = str(
    bindingCandidate(classification.candidate_usml, classification.binding_constraints, usmlCategory) ||
      (classification.candidate_usml || [])[0],
  );
  const regimeNote =
    regime === 'ITAR'
      ? usmlMatch
        ? `Matches USML ${usmlMatch}`
        : 'Matches a USML category'
      : regime === 'uncertain'
      ? undefined
      : 'USML checked, no match';


  const constraints = (classification.binding_constraints || []).map((c: any) => ({
    citation: str(c.citation),
    matchedSpec: humanizeSpecRef(str(c.matched_spec)),
    decontrol: str(c.decontrol_threshold) || 'None by spec change',
    controlText: str(c.control_text),
  }));

  const openItems = (classification.caveats || []).map((c: any) => ({
    missingSpec: str(c.missing_spec),
    assumed: str(c.assumed) || 'Not stated',
    impact: str(c.impact) || 'Impact not recorded.',
    // The classifier flags the direction in `impact`; surface it so the reader can triage.
    couldRaise: /more controlled|stays controlled|no decontrol|raise/i.test(str(c.impact)),
  }));

  const orderOfReview: DesignTicketPayload['orderOfReview'] = [];
  // First: what it is controlled under and for — the answer a compliance reader acts on.
  const controlSentence = controlSentenceOf(classification);
  if (controlSentence) {
    orderOfReview.push({ title: 'Controlled under', body: controlSentence });
  }
  if (classification.order_of_review_note) {
    orderOfReview.push({
      title: 'Order of Review',
      body: str(classification.order_of_review_note),
    });
  }
  if (classification.explanation) {
    orderOfReview.push({ title: 'Why this regime', body: str(classification.explanation) });
  }
  if (classification.matched_user_claim === true) {
    orderOfReview.push({
      title: 'Against what you believed',
      tag: 'confirmed',
      body: 'The grounded review agrees with the regime you expected.',
    });
  } else if (classification.matched_user_claim === false) {
    orderOfReview.push({
      title: 'Against what you believed',
      body: 'The grounded review DIFFERS from the regime you expected — worth reading closely.',
    });
  }

  const rungs: TicketRung[] = ((ladder || {}).rungs || []).map((r: any) => ({
    regime: str(r.regime),
    isCurrent: Boolean(r.is_current),
    reachable: r.reachable !== false,
    note: [str(r.citation), str(r.consequence) || str(r.change_summary)]
      .filter(Boolean)
      .join(' · '),
  }));

  // `embargoed`/`itar_proscribed` may be absent (not checked) rather than false (checked and
  // clear) — only assert a negative when the field is actually a boolean, otherwise say so.
  const boolStatus = (value: any, whenTrue: string, whenFalse: string): string =>
    typeof value === 'boolean' ? (value ? whenTrue : whenFalse) : 'Not checked';

  const licensingBasis = licensing
    ? [
        ...(licensing.basis || []).map((b: any, i: number) => ({
          label: i === 0 ? 'Basis' : ' ',
          value: str(b),
        })),
        {
          label: 'Embargo',
          value: boolStatus(licensing.embargoed, 'Embargoed destination', 'Not embargoed'),
        },
        {
          label: 'ITAR §126.1',
          value: boolStatus(licensing.itar_proscribed, 'Proscribed', 'Not proscribed'),
        },
        ...(licensing.license_exceptions_note
          ? [{ label: 'Exceptions', value: str(licensing.license_exceptions_note) }]
          : []),
      ]
    : [];

  const conversation = (state.conversation || []) as Array<{ role: string; text: string; ts: string }>;
  const trail = conversation
    .filter((turn) => turn.role === 'assistant')
    .slice(0, 12)
    .map((turn) => ({
      time: localClockTime(turn.ts),
      text: str(turn.text).replace(/[#*_`]/g, '').slice(0, 140),
    }));

  // The chat, in order. The stored chat when there is one; otherwise the server's own record of the
  // turns, so a ticket saved before the chat was stored still shows its history (as text).
  const storedChat = savedDesignChatTranscript(savedCase);
  const chatRows: Array<{ sender: 'user' | 'assistant'; text: string; ts: string; files?: string[] }> =
    storedChat.length > 0
      ? storedChat.map((m) => ({
          sender: m.sender === 'user' ? 'user' : 'assistant',
          text: str(m.text),
          ts: str(m.ts),
          files: m.attachedFilenames,
        }))
      : conversation
          .filter((turn) => (turn.role === 'user' || turn.role === 'assistant') && str(turn.text))
          .map((turn) => ({
            sender: turn.role === 'user' ? ('user' as const) : ('assistant' as const),
            text: str(turn.text),
            ts: str(turn.ts),
          }));
  // The latest report turn is the one the ticket already shows in full (as its summary); say so there
  // rather than print the same report again. Only while that report still stands.
  const reportTurn = state.report
    ? findReportTurnIndex(chatRows.map((row) => ({ role: row.sender, text: row.text })))
    : -1;
  const chat: TicketChatMessage[] = chatRows.map((row, index) => ({ ...row, isReport: index === reportTurn }));

  // The backend lists files here after reading them. Older saved tickets may not have that
  // state, but their saved chat still records successfully attached file names.
  const DOCUMENT_KINDS: Record<string, string> = { pdf: 'PDF document', image: 'Image' };
  const recordedDocuments = ((state.documents || []) as any[])
    .filter((doc) => doc && str(doc.filename))
    .map((doc) => ({
      name: str(doc.filename),
      meta: [
        DOCUMENT_KINDS[str(doc.kind)] || 'File',
        localCalendarDate(doc.added_at) && `added ${localCalendarDate(doc.added_at)}`,
      ]
        .filter(Boolean)
        .join(' · '),
    }));
  const documentNames = new Set(recordedDocuments.map((document) => document.name.toLowerCase()));
  const transcriptDocuments = chatRows.reduce((documents: Array<{ name: string; meta: string }>, row) => {
    (row.files || []).forEach((file) => {
      const name = str(file);
      if (!name || documentNames.has(name.toLowerCase())) return;
      documentNames.add(name.toLowerCase());
      documents.push({
        name: name,
        meta: ['Attached document', localCalendarDate(row.ts) && `added ${localCalendarDate(row.ts)}`]
          .filter(Boolean)
          .join(' · '),
      });
    });
    return documents;
  }, []);
  const documents = recordedDocuments.concat(transcriptDocuments);

  const summary = str(report.executive_summary);
  // The Final Design Summary (Stage 4). `state.report` is reset to null when a spec change
  // invalidates the classification, so a stale report never counts.
  // What the report's caveats should say: real limitations of what WAS analysed. Reports saved
  // before the prompt was tightened also carry (a) the classification's open specs, copied in
  // verbatim — they are already the Open items panel — (b) a note that an optional analysis wasn't
  // run, and (c) the standing "advisory only" line, which the page disclaimer already says.
  const openSpecNames = openItems.map((i: any) => norm(i.missingSpec)).filter(Boolean);
  const reportCaveats = ((report.caveats || []) as any[])
    .map(str)
    .filter(Boolean)
    .filter((c) => {
      const lc = norm(c);
      if (openSpecNames.some((name: string) => lc.startsWith(name) && lc.includes('was not provided'))) {
        return false;
      }
      if (/^this is advisory/.test(lc)) return false;
      if (/^no (delta|design[- ]change|licensing check)\b/.test(lc)) return false;
      return true;
    });
  const hasReport = Boolean(
    state.report && (str(report.executive_summary) || str(report.recommended_next_step)),
  );

  // A design-change analysis only exists when the user asked for one — it is no longer a stage.
  const designChanges: TicketDesignChanges | null = delta
    ? {
        target: str(delta.target_regime),
        reachable: delta.reachable !== false,
        summary:
          str(delta.reachable === false ? delta.infeasible_reason : delta.explanation) || undefined,
        changes: (delta.changes || []).map((ch: any) => ({
          description: str(ch.description),
          details: [
            ch.parameter && (ch.current_value || ch.proposed_value)
              ? `${humanizeKey(str(ch.parameter))}: ${str(ch.current_value) || '?'} → ${
                  str(ch.proposed_value) || '?'
                }`
              : '',
            ch.constraint_citation ? `Addresses ${str(ch.constraint_citation)}` : '',
            str(ch.rationale),
            ch.feasibility ? `Feasibility: ${str(ch.feasibility)}` : '',
            ch.destroys_capability ? 'Would break a capability you said the product must keep.' : '',
          ].filter(Boolean),
        })),
        resultingRegime: str(delta.resulting_regime) || undefined,
        cost: str(delta.functional_impact) || undefined,
        residual: (delta.residual_constraints || []).map(str).filter(Boolean),
      }
    : null;

  return {
    ticketName: str(savedCase?.ticketName) || 'Design Assistant',
    caseId: str(savedCase?._id) || undefined,
    // A regime on the result means a classification ran; a ticket saved before one has none.
    classified: Boolean(str(classification.current_regime)),
    reference: savedCase?.autoId ? str(savedCase.autoId) : undefined,
    status: str(savedCase?.status) || 'In Progress',
    owner: [savedCase?.owner?.firstName, savedCase?.owner?.lastName].filter(Boolean).join(' '),
    team: str(savedCase?.groupName),
    savedAt: str(savedCase?.lastUpdate),
    sharedWith: (savedCase?.sharedWith || []).length,
    // Export Workflow's own metadata-strip vocabulary (ConsolidatedResults.tsx:2420-2492),
    // reused verbatim so the two sibling tools' tickets share a vocabulary.
    frequency: str(savedCase?.frequency) || 'Once',
    ticketType: DESIGN_TICKET_DOC_TYPE,
    // The headline is the product's NAME. The description is free text the router sometimes fills
    // with a single spec ("NETD 25 mK"), which made a saved ticket headline read as a spec, so it
    // is only a last resort.
    productName:
      factValue(state, 'product_name') ||
      factValue(state, 'product_type') ||
      factValue(state, 'product_description') ||
      str(savedCase?.classificationValue),
    regime: regime,
    regimeNote: regimeNote,
    // NOTE (Leo found a real case this doesn't handle well): candidate_eccns/candidate_usml
    // are documented as "considered" (pyapi/app/agents/export_design/schemas.py:198-199), not
    // necessarily binding — but binding_constraints[0] isn't a safe replacement either: it
    // often lists an underlying component-level control (e.g. a focal-plane-array entry)
    // *before* the finished-item control it triggers, so "the first binding constraint" can
    // be the wrong headline code for a multi-part product (verified against this file's own
    // fixture below, where constraints[0] is the component control, not the camera's own
    // 6A003.b.4.b that candidate_eccns[0] correctly surfaces). For a saved assessment covering
    // multiple product variants with genuinely different outcomes (Leo's drone case), no
    // single field here can correctly stand in for "the" primary control — that needs a
    // product decision (show the worst case across variants? flag multiplicity explicitly in
    // the UI?) rather than a different array index.
    // Whatever the headline code is, it must belong to the determined regime: both candidate
    // lists hold codes that were only *considered*, so an ECCN-first pick can put a rejected
    // ECCN on an ITAR or EAR99 ticket (the fixture below considers USML XII on an EAR result).
    // Within the regime, the considered code that binds leads, not the first one looked at (S12
    // R-10: a production ticket read "ECCN 3A002.h" with 4A994.a binding); see primaryControlOf.
    primaryControl:
      regime === 'ITAR' || regime === 'EAR-listed'
        ? primaryControlOf(classification) || '—'
        : regime === 'EAR99'
        ? 'EAR99'
        : constraints[0]?.citation || '—',
    reasonsForControl: controlReasonsOf(classification),
    destination: factValue(state, 'destination_country') || '—',
    intendedUse: intendedUse,
    licenceRequired: licensing ? licensing.license_required ?? null : null,
    licensingRan: Boolean(licensing),
    // For an ECCN with known reasons, what it is controlled for IS the licence answer, and the agency
    // detail follows it (mirrors _licensing_implication_lines in the backend).
    licensingImplication: licencePostureOf(classification)
      ? `${licencePostureOf(classification)} It is licensed by the U.S. Department of Commerce ` +
        'through the Bureau of Industry and Security (BIS), with applications filed in SNAP-R.'
      : LICENSING_IMPLICATION[regime] || LICENSING_IMPLICATION.uncertain,
    confidence:
      (hasReport && str(report.overall_confidence)) || str(classification.confidence) || 'Not stated',
    executiveSummary: hasReport && summary ? summary.split(/\n{2,}/).filter(Boolean) : [],
    hasReport: hasReport,
    recommendedNextStep: (hasReport && str(report.recommended_next_step)) || undefined,
    reportCaveats: hasReport ? reportCaveats : [],
    licensingSummary: (hasReport && str(report.licensing_summary)) || undefined,
    designChanges: designChanges,
    specs: specs,
    orderOfReview: orderOfReview,
    openItems: openItems,
    unavailableSpecs: (meta.unavailable_specs || []).map(str),
    constraints: constraints,
    verifierRan: Boolean(verifier),
    // Only an explicit `supported: true` counts as verified — a missing or malformed verdict
    // (e.g. an older saved ticket) must not be reported as independent confirmation.
    verifierSupported: verifier?.supported === true,
    verifierNote: verifier
      ? verifier.supported === false
        ? `Issues raised: ${(verifier.issues || []).join('; ') || 'see the analysis.'}`
        : verifier.supported === true
        ? verifier.unstated_spec_dependency
          ? 'Supported, with an unstated-spec dependency raised — see the open items.'
          : 'Supported, with no issues raised.'
        : 'Independent verification ran, but no definitive verdict was recorded.'
      : 'No independent verification was recorded for this assessment.',
    options: (hasReport ? report.options || [] : []).map((o: any) => ({
      label: str(o.label),
      targetRegime: str(o.target_regime),
      changeSummary: str(o.change_summary),
      feasibility: str(o.feasibility) || undefined,
      tradeoff: str(o.tradeoff) || undefined,
      recommended: Boolean(o.recommended),
    })),
    // A note ABOUT the options list ("no design-change analysis has been run, ask…") means nothing
    // without one, and reports saved before design changes left the guided flow carry it anyway.
    optionsNote:
      (hasReport && (report.options || []).length > 0 && str(report.options_note)) || undefined,
    ladder: hasReport ? rungs : [],
    licensingBasis: licensingBasis,
    documents: documents,
    trail: trail,
    conversation: chat,
    // Present in state, but only meaningful once the backend keeps them — see the component,
    // which labels both `proposed`.
    supersededNote: (meta.superseded_classification || []).length
      ? `This assessment replaced ${(meta.superseded_classification || []).length} earlier determination(s), invalidated by a spec change.`
      : undefined,
    regulationEdition: undefined,
  };
};

// --- Sample saved case, for /export-design/ticket-preview.
//
// This is the DOCUMENT SHAPE, not a render fixture: the preview runs it through
// readDesignTicket above, so the preview exercises the same path a real saved ticket will.
// Values are from a real local run of the thermal-camera scenario. If the workflow_state
// shape drifts, the preview breaks — which is the point.
const SAMPLE_WORKFLOW_STATE = {
  facts: {
    product_name: { value: 'TG-450X thermal camera', source: 'router.map_specs', version: 1 },
    product_type: { value: 'uncooled thermal imaging camera', source: 'router.map_specs', version: 1 },
    product_description: {
      value: 'Uncooled thermal imaging camera for commercial building inspection',
      source: 'router.map_specs',
      version: 1,
    },
    intended_use: { value: 'commercial building energy audits', source: 'router.map_specs', version: 1 },
    destination_country: { value: 'Spain', source: 'router.map_specs', version: 1 },
    current_regime: { value: 'EAR-listed', source: 'classifier', version: 1 },
    candidate_eccns: { value: '6A003, 6A002', source: 'classifier', version: 1 },
  },
  spec_sheet: {
    netd_mk: { value: '25', source: 'router.map_specs', version: 1 },
    focal_plane_array: { value: '640x480 microbolometer', source: 'router.map_specs', version: 1 },
    frame_rate_hz: { value: '30', source: 'router.map_specs', version: 1 },
    lens_focal_length_mm: { value: '50', source: 'attachment.datasheet', version: 1 },
    lens_material: { value: 'germanium', source: 'attachment.datasheet', version: 1 },
    spectral_band_micron: { value: '8-14', source: 'router.map_specs', version: 1 },
  },
  classification: {
    result: {
      current_regime: 'EAR-listed',
      candidate_eccns: ['6A003.b.4.b', '6A002.a.3.f'],
      candidate_usml: ['XII'],
      matched_user_claim: true,
      order_of_review_note:
        'USML Category XII(c) was checked first: the entries reached require a military application or specially-designed status, and a commercial building-inspection camera on a commercial catalog core meets neither. The review therefore moves to the CCL, where 6A002.a.3.f describes the array and 6A003.b.4.b catches a camera incorporating it. EAR99 is not reached because an ECCN matched.',
      explanation:
        'The 640x480 microbolometer focal plane array meets 6A002.a.3.f, and a camera incorporating such an array is controlled by 6A003.b.4.b unless one of the Note 3 conditions applies.',
      confidence: 'medium',
      binding_constraints: [
        {
          citation: '6A002.a.3.f',
          control_type: 'parameter',
          matched_spec: 'focal_plane_array',
          reason_for_control: 'NS, RS',
          decontrol_threshold: 'Fewer elements than the threshold — not viable without losing the product’s purpose',
          control_text:
            'Non-"space-qualified" non-linear (2-dimensional) infrared "focal plane arrays" based on \'microbolometer\' material having individual elements with an unfiltered response…',
        },
        {
          citation: '6A003.b.4.b',
          control_type: 'parameter',
          matched_spec: 'focal_plane_array',
          reason_for_control: 'NS, RS',
          decontrol_threshold:
            'Meet a Note 3.b condition — fixed lens, no direct-view display, IFOV above threshold',
          control_text:
            'Imaging cameras incorporating "focal plane arrays" controlled by 6A002.a.3.f… Note 3: 6A003.b.4.b does not control imaging cameras having any of the following…',
        },
      ],
      caveats: [
        {
          missing_spec: 'instantaneous field of view (mrad)',
          assumed: 'Wider than the Note 3.b threshold',
          impact:
            'If the IFOV is narrower, Note 3.b stops applying and the camera stays controlled at 6A003.b.4.b with no decontrol route.',
        },
        {
          missing_spec: 'lens fixed or removable',
          assumed: 'Permanently fixed, non-removable',
          impact: 'A removable lens defeats the Note 3.b carve-out entirely, leaving it more controlled.',
        },
        {
          missing_spec: 'direct-view display present',
          assumed: 'No integrated direct-view display',
          impact: 'Affects which limb of Note 3.b is read, not the regime itself.',
        },
      ],
    },
    verifier: {
      supported: true,
      unstated_spec_dependency: true,
      missing_specs: ['instantaneous field of view (mrad)', 'lens fixed or removable'],
      issues: [],
    },
  },
  ladder: {
    current_regime: 'EAR-listed',
    rungs: [
      { regime: 'ITAR', reachable: false, citation: 'USML XII(c)', consequence: 'Not reachable — no USML entry matched' },
      {
        regime: 'EAR-listed',
        is_current: true,
        reachable: true,
        citation: '6A003.b.4.b',
        consequence: 'NS Column 2, RS — licence needed for most destinations, not Spain',
      },
      {
        regime: 'EAR99',
        reachable: true,
        change_summary: 'Meet the Note 3.b carve-out',
        consequence: 'No licence for most destinations — substantial relief',
      },
    ],
  },
  licensing: {
    destination_country: 'Spain',
    regime: 'EAR-listed',
    license_required: false,
    embargoed: false,
    itar_proscribed: false,
    basis: [
      'Country Chart: NS Column 2 does not require a licence for Spain.',
      'RS controls do not apply to Spain for this entry.',
    ],
  },
  report: {
    executive_summary:
      'The camera is EAR-listed — not ITAR, and not EAR99. Its 640×480 microbolometer focal plane array meets 6A002.a.3.f, and a camera incorporating such an array is caught by 6A003.b.4.b.\n\nExport to Spain does not require a licence on the facts checked. Three Note 3.b decontrol conditions were never supplied, so the determination rests on stated assumptions — two of them could move the product to a more controlled position if the real values differ.',
    overall_confidence: 'medium',
    recommended_next_step: 'Confirm the three Note 3.b conditions with engineering.',
    options: [
      {
        label: 'Confirm the Note 3.b conditions',
        target_regime: 'EAR99',
        change_summary:
          'None — a documentation exercise, not a redesign. Confirm the lens is non-removable, record the IFOV, confirm no direct-view display.',
        feasibility: 'High. No engineering work if the assumptions hold.',
        tradeoff: 'None, unless a value comes back outside the carve-out.',
        recommended: true,
      },
      {
        label: 'Reduce array resolution below the threshold',
        target_regime: 'EAR99',
        change_summary: 'Drop below the 6A002.a.3.f element count.',
        feasibility: 'Low. Requires a different sensor.',
        tradeoff:
          'Destroys the product’s stated purpose — building-inspection imagery at this sensitivity is the reason it exists.',
      },
    ],
    caveats: [
      'The IFOV figure was declared unavailable, so the determination assumes it falls outside the Note 3.b carve-out.',
      'Whether the lens is removable was assumed, not confirmed; a removable lens would leave the camera more controlled.',
    ],
  },
  conversation: [
    { role: 'user', text: 'Classify an uncooled thermal imaging camera…', ts: '2026-09-22T14:36:49Z' },
    { role: 'assistant', text: 'Noted the product and six specs; classifying against ITAR and the EAR.', ts: '2026-09-22T14:38:10Z' },
    { role: 'assistant', text: 'Classification complete — EAR-listed under ECCN 6A003.b.4.b.', ts: '2026-09-22T14:41:02Z' },
    { role: 'assistant', text: 'Verification complete — supported, with an unstated-spec dependency raised.', ts: '2026-09-22T14:43:30Z' },
    { role: 'assistant', text: 'Regime ladder laid out: ITAR blocked, EAR99 reachable via Note 3.b.', ts: '2026-09-22T14:46:12Z' },
    { role: 'assistant', text: 'Licensing for Spain: no licence required on the inputs checked.', ts: '2026-09-22T14:49:55Z' },
  ],
  documents: [
    { attachment_id: 'sample-1', filename: 'TG-450X-datasheet.pdf', kind: 'pdf', added_at: '2026-09-22T14:36:49Z' },
  ],
  _meta: {
    unavailable_specs: ['the IFOV figure'],
    superseded_classification: [{}],
  },
};

export const SAMPLE_SAVED_CASE: Record<string, any> = {
  ticketName: 'TG-450X thermal camera — Spain',
  status: 'Complete',
  docType: DESIGN_TICKET_DOC_TYPE,
  workflowType: DESIGN_TICKET_WORKFLOW_TYPE,
  activityType: 'workflow',
  owner: { firstName: 'Zodzwai', lastName: 'Kudyachete', email: 'zodzwai.k@bitedata.io' },
  groupName: 'BITE Export',
  lastUpdate: '2026-09-22',
  sharedWith: ['leo@bitedata.io', 'anne@bitedata.io'],
  frequency: 'Once',
  classificationValue: 'Uncooled thermal imaging camera for commercial building inspection',
  workflowData: [{ _workflowStateJson: JSON.stringify(SAMPLE_WORKFLOW_STATE) }],
};

/** The sample case reduced to how far a real assessment can get, for the preview route
 *  (/export-design/ticket-preview?variant=...). Same document shape, fewer keys — so it exercises
 *  the same reader a saved ticket goes through.
 *    full            every stage, plus the report's extras (the default)
 *    classification  stopped after the Specification Map: no report, no licensing, no ladder
 *    licensing       asked for a licensing check but never generated the report
 *    design-changes  asked for a design-change analysis but never generated the report
 *    clean           the thinnest real ticket: EAR99, nothing to map, no intended use captured */
export type TicketPreviewVariant =
  | 'full'
  | 'classification'
  | 'licensing'
  | 'design-changes'
  | 'clean';

export const sampleSavedCase = (variant: TicketPreviewVariant = 'full'): Record<string, any> => {
  if (variant === 'full') return SAMPLE_SAVED_CASE;
  const state: Record<string, any> = JSON.parse(JSON.stringify(SAMPLE_WORKFLOW_STATE));
  delete state.report;
  delete state.ladder;
  if (variant !== 'licensing') delete state.licensing;
  if (variant === 'classification' || variant === 'licensing') delete state.delta;
  if (variant === 'classification') delete state.facts.destination_country;
  if (variant === 'clean') {
    delete state.licensing;
    delete state.delta;
    delete state.facts.destination_country;
    delete state.facts.intended_use;
    state.classification = {
      result: {
        current_regime: 'EAR99',
        candidate_eccns: [],
        candidate_usml: [],
        binding_constraints: [],
        caveats: [],
        order_of_review_note:
          'USML was checked first and nothing matched; no CCL entry describes the product, so it falls to EAR99.',
        explanation: 'No control in the USML or the CCL applies to this product as specified.',
        confidence: 'high',
      },
      verifier: { supported: true, unstated_spec_dependency: false, issues: [] },
    };
  }
  if (variant === 'design-changes') {
    delete state.facts.destination_country;
    state.delta = {
      target_regime: 'EAR99',
      reachable: true,
      explanation: 'Meeting the Note 3.b carve-out takes the camera out of 6A003.b.4.b.',
      changes: [
        {
          description: 'Fix the lens permanently and remove the direct-view display',
          parameter: 'lens_fixed',
          current_value: 'removable',
          proposed_value: 'permanently fixed',
          constraint_citation: '6A003.b.4.b Note 3',
          rationale: 'Satisfies the Note 3.b conditions.',
          feasibility: 'High',
        },
      ],
      resulting_regime: 'EAR99',
      functional_impact: 'No interchangeable lenses.',
      residual_constraints: ['End-use and end-user controls still apply.'],
    };
  }
  return {
    ...SAMPLE_SAVED_CASE,
    ticketName: `TG-450X thermal camera — ${variant}`,
    status: 'In Progress',
    workflowData: [{ _workflowStateJson: JSON.stringify(state) }],
  };
};
